// THE LETTER STEP'S LAYOUT RULES (WEB-PARITY-5) — which of the app's sentences a SERVED fact selects, how a served
// figure is printed, and how the overlay's checklist ticks. Pure: nothing here decides a fact or computes a figure the
// server owns; each rule is the app's, cited. The overlay's clock is UI-only state — the app's own animation
// (`LetterGenerationModel.run`, LetterGenerationOverlay.swift:108-156), which reads no fact but whether the job is done.

import type { LetterJobView, Money } from "@contract";
import { money } from "../../format";
import {
  FINAL_STEP_MESSAGES, sayG, sayGWith, type GenerateWordingId,
} from "./generateWording";

type Failed = Extract<LetterJobView, { status: "failed" }>["failure"];

// ── the honest fork (GenerateView.swift:150-171, :393-417) ────────────────────────────────────────────────────────

/** `NonDisclosure.states` (NonDisclosure.swift:62-64): where sale prices are not public, which only chooses the thin-data
 *  card's lead sentence. Pinned to the Swift set by `test/parity5.test.tsx`. */
export const NON_DISCLOSURE_STATES: readonly string[] = ["AK", "ID", "KS", "MS", "MO", "MT", "NM", "TX", "UT", "WY"];

/** `NonDisclosure.applies(to:)` (NonDisclosure.swift:66-69). */
export function nonDisclosure(state: string | null | undefined): boolean {
  const s = (state ?? "").trim().toUpperCase();
  return s.length === 2 && NON_DISCLOSURE_STATES.includes(s);
}

export type ForkKind = "thinData" | "noCase";

/** `forkTitle` (:370-375). */
export function forkTitle(kind: ForkKind): string {
  return sayG(kind === "thinData" ? "gen.fork.thinData.title" : "gen.fork.noCase.title");
}

/** `forkBody` (:376-390), over A-caseLooksWeak's own count of nearby sales (the app's `usableComps`). */
export function forkBody(kind: ForkKind, nearbySales: number, state: string | null): string {
  if (kind === "noCase") return sayGWith("gen.fork.noCase.body", { n: nearbySales });
  const salesPhrase = nearbySales === 0
    ? sayG("gen.fork.sales.none")
    : sayGWith("gen.fork.sales.some", { n: nearbySales, sales: sayG(nearbySales === 1 ? "gen.fork.sales.sale" : "gen.fork.sales.sales") });
  const lead = sayGWith(nonDisclosure(state) ? "gen.fork.lead.nonDisclosure" : "gen.fork.lead.default", { salesPhrase });
  return sayGWith("gen.fork.thinData.body", { lead });
}

// ── "Your case" (GenerateView.swift:591-863) ─────────────────────────────────────────────────────────────────────

/** `GenerateView.tidyStreet` (:569-579): the street line before the first comma, title-cased, with a word that starts
 *  with a digit (a house number, "49th") left as it is. */
export function tidyStreet(address: string): string {
  const street = (address.split(",")[0] ?? address).trim();
  if (street === "") return "(no address)";
  return street
    .toLowerCase()
    .split(" ")
    .filter((w) => w !== "")
    .map((w) => (/^[0-9]/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

/** `SubjectProperty.basisTagged` (Models.swift:1245-1248): a figure names its basis only where the basis is fractional. */
export function basisTagged(figure: Money<"market"> | Money<"assessed">, fractional: boolean): string {
  if (!fractional) return money(figure);
  return sayGWith("gen.basis.tagged", {
    value: money(figure),
    basis: sayG(figure.basis === "market" ? "gen.basis.market" : "gen.basis.assessed"),
  });
}

/** "\(amount) (−\(percent)%)" (:643), over the server's `ask.estimatedReduction` (`Appeal.estimatedReduction`'s percent
 *  is an Int, so the served figure prints whole). */
export function reductionValue(amount: Money<"assessed">, percent: number): string {
  return sayGWith("gen.case.reductionValue", { amount: money(amount), percent: Math.round(percent) });
}

// ── a letter that did not finish (GenerateView.swift:966-1052; LetterService.swift:309-365) ─────────────────────

/** `RefusalRoute` (LetterService.swift:138-156). */
export type RefusalRoute = "comps" | "condition" | "value";

/** Where each route's fix lives on the website: the app's Comps step (`onReviewComps`), its Reasons step — Condition —
 *  (`onStrengthen`) and its Value step (`onEditValue`), GenerateView.swift:1046-1052. */
export const ROUTE_PATH: Readonly<Record<RefusalRoute, string>> = { comps: "/comps", condition: "/condition", value: "/verdict" };

/** `LetterServiceError.isQuestion` (:309-323): a refusal that has an answer is a question — `INSUFFICIENT_BASIS`. The
 *  app's other question, the estimate-corroborated no-case, has had no path since row 427, and the server's
 *  `NO_REDUCTION_CASE` does not say which no-case it was, so it stays a failure (the app's acquisition-value reading). */
export function failureIsQuestion(failure: Failed): boolean {
  return failure.code === "INSUFFICIENT_BASIS";
}

/** `LetterServiceError.refusalRoutes` (:332-365), the app's list for each refusal, in the app's order, read off the
 *  server's code and route. A route the server sends for something nothing in the appeal can fix (`retry`, `support`)
 *  and the no-case (`noCaseVerdict`) carry none, as the app's do. */
export function failureRows(failure: Failed): readonly RefusalRoute[] {
  switch (failure.code) {
    case "INSUFFICIENT_BASIS":
      // thin data leads with a sale the owner knows of; the rest with the home's condition (:331-332).
      return failure.route === "comps" ? ["comps", "condition", "value"] : ["condition", "value", "comps"];
    case "BELOW_RECENT_PURCHASE":
      return ["value", "condition"];
    default:
      return [];
  }
}

// ── the overlay's checklist (GenerateView.swift:1542-1601; LetterGenerationOverlay.swift:108-156) ─────────────────

export type Tone = "formal" | "respectful" | "direct";

export const TONES: readonly Tone[] = ["formal", "respectful", "direct"];

const TONE_TITLE: Readonly<Record<Tone, GenerateWordingId>> = {
  formal: "gen.tone.formal", respectful: "gen.tone.respectful", direct: "gen.tone.direct",
};
const TONE_BLURB: Readonly<Record<Tone, GenerateWordingId>> = {
  formal: "gen.tone.formal.blurb", respectful: "gen.tone.respectful.blurb", direct: "gen.tone.direct.blurb",
};

export function toneTitle(tone: Tone): string {
  return sayG(TONE_TITLE[tone]);
}

export function toneBlurb(tone: Tone): string {
  return sayG(TONE_BLURB[tone]);
}

/** A tone read off the address bar: the three the server takes, else the app's default (`letterTone ?? .formal`). */
export function toneFrom(value: string | null): Tone {
  return value === "respectful" || value === "direct" ? value : "formal";
}

/** What the checklist is built from: the appeal's own counts, all served. */
export interface OverlayFacts {
  /** A-caseLooksWeak's `nearbySales` — the server's usable comps, the app's `comps.filter(\.isUsable).count`. */
  readonly usableComps: number;
  readonly photos: number;
  /** The condition note on file is not blank. */
  readonly hasCondition: boolean;
  /** A ground is ticked, or the record-error note is not blank (`reasons` / `reasonNotes`, :1390). */
  readonly hasReasons: boolean;
  readonly tone: Tone;
}

export type RowIcon = "house" | "bars" | "scale" | "list" | "wrench" | "photo" | "pencil" | "doc";

export interface OverlayRow {
  readonly id: GenerateWordingId;
  readonly text: string;
  readonly icon: RowIcon;
}

/** `buildGenerationModel` (:1369-1428): the rows, in the app's order, each only where the appeal has its subject. */
export function overlayRows(f: OverlayFacts): OverlayRow[] {
  const rows: OverlayRow[] = [{ id: "build.row.property", text: sayG("build.row.property"), icon: "house" }];
  if (f.usableComps > 0) {
    rows.push({
      id: "build.row.comps",
      text: sayGWith("build.row.comps", { n: f.usableComps, sales: sayG(f.usableComps === 1 ? "build.row.comps.sale" : "build.row.comps.sales") }),
      icon: "bars",
    });
    rows.push({ id: "build.row.compare", text: sayG("build.row.compare"), icon: "scale" });
  } else {
    rows.push({ id: "build.row.assessed", text: sayG("build.row.assessed"), icon: "scale" });
  }
  if (f.hasReasons) rows.push({ id: "build.row.reasons", text: sayG("build.row.reasons"), icon: "list" });
  if (f.hasCondition) rows.push({ id: "build.row.condition", text: sayG("build.row.condition"), icon: "wrench" });
  if (f.photos > 0) {
    rows.push({
      id: "build.row.photos",
      text: sayGWith("build.row.photos", { n: f.photos, exhibits: sayG(f.photos === 1 ? "build.row.photos.exhibit" : "build.row.photos.exhibits") }),
      icon: "photo",
    });
  }
  // `toneTitle = (appeal.letterTone ?? .formal).title.lowercased()` (:1377).
  rows.push({ id: "build.row.tone", text: sayGWith("build.row.tone", { tone: toneTitle(f.tone).toLowerCase() }), icon: "pencil" });
  rows.push({ id: "build.row.format", text: sayG("build.row.format"), icon: "doc" });
  return rows;
}

/** The app's pacing (`minTotal: 24`, :1410; the hold loop's 120 ms tick, 30 ticks to a rotation, LO:124-136; the
 *  90-second long-wait threshold, LO:43). */
export const OVERLAY_MIN_TOTAL_MS = 24_000;
export const OVERLAY_TICK_MS = 120;
export const OVERLAY_ROTATE_MS = 30 * OVERLAY_TICK_MS;
export const OVERLAY_LONG_WAIT_MS = 90_000;

export type RowState = "pending" | "active" | "done";

export interface OverlayState {
  readonly rows: readonly RowState[];
  /** The last row's words while it holds: the server's queue, else the rotation, else its own. */
  readonly lastLabel: GenerateWordingId | null;
  readonly longWait: boolean;
  readonly allDone: boolean;
  /** Done rows over all rows: the progress bar (`progress`, LO:87-90). */
  readonly progress: number;
}

/**
 * `LetterGenerationModel.run` (LO:108-156) as a function of time: row i is active from its turn and done at
 * `minTotal × (i + 1) / n`; the last row holds — "Queued — your letter is next in line" while the server says the job
 * is queued, else the final messages one every 3.6 s — until the work is done AND its own turn has come; 90 s into the
 * hold the long-wait notice replaces the note. Done when the last row is.
 */
export function overlayState(n: number, elapsedMs: number, workDone: boolean, queued: boolean): OverlayState {
  const turn = (i: number): number => (OVERLAY_MIN_TOTAL_MS * (i + 1)) / n;
  const rows: RowState[] = [];
  for (let i = 0; i < n; i++) {
    const from = i === 0 ? 0 : turn(i - 1);
    const last = i === n - 1;
    const done = last ? workDone && elapsedMs >= turn(i) : elapsedMs >= turn(i);
    rows.push(done ? "done" : elapsedMs >= from ? "active" : "pending");
  }
  const holding = n > 0 && rows[n - 1] === "active" && elapsedMs >= OVERLAY_MIN_TOTAL_MS;
  let lastLabel: GenerateWordingId | null = null;
  if (holding) {
    lastLabel = queued
      ? "build.queued"
      : FINAL_STEP_MESSAGES[Math.floor((elapsedMs - OVERLAY_MIN_TOTAL_MS) / OVERLAY_ROTATE_MS) % FINAL_STEP_MESSAGES.length];
  }
  const doneCount = rows.filter((r) => r === "done").length;
  return {
    rows,
    lastLabel,
    longWait: holding && elapsedMs - OVERLAY_MIN_TOTAL_MS >= OVERLAY_LONG_WAIT_MS,
    // With no rows to tick (the step's counts never arrived), the letter being done is the whole of it.
    allDone: n === 0 ? workDone : rows[n - 1] === "done",
    progress: n === 0 ? 0 : doneCount / n,
  };
}
